import { divide, isEmpty, multiply } from 'lodash';
import type { IObjective } from './Objective.interface';
import ObjectiveNeedsAndObjectives from './ObjectiveNeedsAndObjectives';
import ObjectiveRequirementsAndObjectives from './ObjectiveRequirementsAndObjectives';
import ObjectiveActingAsAGuarantor from './ObjectiveActingAsAGuarantor';
import ObjectivePossibleAdverseChangesToFinancialSituation from './ObjectivePossibleAdverseChangesToFinancialSituation';
import ObjectivePossibleBeneficialChangesToFinancialSituation from './ObjectivePossibleBeneficialChangesToFinancialSituation';
import ObjectiveExitStrategy from './ObjectiveExitStrategy';
import ObjectiveRateType from './ObjectiveRateType';
import ObjectiveRepaymentType from './ObjectiveRepaymentType';
import ObjectiveProductType from './ObjectiveProductType';
import ObjectiveConflictsBetweenLoanFeatures from './ObjectiveConflictsBetweenLoanFeatures';
import ObjectiveSignedDocuments from './ObjectiveSignedDocuments';
import ObjectiveWhatIsImportantForYou from './ObjectiveWhatIsImportantForYou';
import ObjectiveTermOfCreditSought from './ObjectiveTermOfCreditSought';
import ObjectiveFinancialSecurity from './ObjectiveFinancialSecurity';
import ObjectiveCreditHistory from './ObjectiveCreditHistory';
import ObjectiveHealth from './ObjectiveHealth';
import ObjectiveAdditionalServices from './ObjectiveAdditionalServices';
import ObjectiveProductRequirements from './ObjectiveProductRequirements';
import ObjectiveArchive from './ObjectiveArchive';
import { LevelOption } from '../global/LevelOption.enum';
import { YesNoOption } from '../global/YesNoOption.enum';

export default class Objective implements IObjective {
  needsAndObjectives: ObjectiveNeedsAndObjectives;
  requirementsAndObjectives: ObjectiveRequirementsAndObjectives;
  actingAsAGuarantor: ObjectiveActingAsAGuarantor;
  possibleAdverseChangesToFinancialSituation: ObjectivePossibleAdverseChangesToFinancialSituation;
  possibleBeneficialChangesToFinancialSituation: ObjectivePossibleBeneficialChangesToFinancialSituation;
  exitStrategy: ObjectiveExitStrategy;
  rateType: ObjectiveRateType;
  repaymentType: ObjectiveRepaymentType;
  productType: ObjectiveProductType;
  conflictsBetweenLoanFeatures: ObjectiveConflictsBetweenLoanFeatures;
  signedDocuments: ObjectiveSignedDocuments;
  otherComments: string;
  whatIsImportantForYou: ObjectiveWhatIsImportantForYou;
  termOfCreditSought: ObjectiveTermOfCreditSought;
  financialSecurity: ObjectiveFinancialSecurity;
  creditHistory: ObjectiveCreditHistory;
  health: ObjectiveHealth;
  additionalServices: ObjectiveAdditionalServices;
  productRequirements: ObjectiveProductRequirements;
  archive: ObjectiveArchive;

  getObjectiveFinancialSecurityTotal() {
    let out = 0;
    if (this.financialSecurity?.levelOfFinancialExperience?.name === LevelOption.low.name) {
      out += 3;
    }
    if (this.financialSecurity?.levelOfFinancialExperience?.name === LevelOption.medium.name) {
      out += 2;
    }
    if (this.financialSecurity?.levelOfFinancialExperience?.name === LevelOption.high.name) {
      out += 1;
    }
    if (
      this.financialSecurity?.concernedAboutMovementsInInterestRates?.name ===
      LevelOption.low.name
    ) {
      out += 1;
    }
    if (
      this.financialSecurity?.concernedAboutMovementsInInterestRates?.name ===
      LevelOption.medium.name
    ) {
      out += 2;
    }
    if (
      this.financialSecurity?.concernedAboutMovementsInInterestRates?.name ===
      LevelOption.high.name
    ) {
      out += 3;
    }
    if (
      this.financialSecurity?.concernedAboutYourCurrentJobSecurity?.name ===
      LevelOption.low.name
    ) {
      out += 1;
    }
    if (
      this.financialSecurity?.concernedAboutYourCurrentJobSecurity?.name ===
      LevelOption.medium.name
    ) {
      out += 3;
    }
    if (
      this.financialSecurity?.concernedAboutYourCurrentJobSecurity?.name ===
      LevelOption.high.name
    ) {
      out += 5;
    }
    if (
      this.financialSecurity?.concernedAboutNegativePropertyValue?.name === LevelOption.low.name
    ) {
      out += 1;
    }
    if (
      this.financialSecurity?.concernedAboutNegativePropertyValue?.name ===
      LevelOption.medium.name
    ) {
      out += 2;
    }
    if (
      this.financialSecurity?.concernedAboutNegativePropertyValue?.name ===
      LevelOption.high.name
    ) {
      out += 3;
    }
    if (this.financialSecurity?.haveAnEmergencyFund?.name === YesNoOption.no.name) {
      out += 5;
    }
    if (
      this.financialSecurity?.ableToMaintainYourCommitmentsAndLifestyle?.name ===
      YesNoOption.no.name
    ) {
      out += 5;
    }
    if (this.financialSecurity?.haveAdequateInsurance?.name === YesNoOption.no.name) {
      out += 5;
    }
    if (this.creditHistory?.applicantHadProblems?.name === YesNoOption.yes.name) {
      out += 5;
    }
    if (this.creditHistory?.applicantBeenOfficerOrShareholder?.name === YesNoOption.yes.name) {
      out += 3;
    }
    if (this.creditHistory?.applicantHaveUnsatisfiedJudgements?.name === YesNoOption.yes.name) {
      out += 3;
    }
    let worstScenario = 40;
    for (let a = 0; a < this.creditHistory?.bankruptcy?.length; a += 1) {
      worstScenario += 10;
      const applicant = this.creditHistory?.bankruptcy[a];
      if (applicant?.response?.name === YesNoOption.yes.name) {
        out += 10;
      }
      if (applicant?.response?.name === YesNoOption.yes_discharged.name) {
        out += 7;
      }
    }
    const index = divide(100, worstScenario);
    const result = multiply(index, out);
    return result;
  }

  static build = (data: IObjective) => new Objective(data);
  static blank = () => new Objective();

  /**
   * Constructor
   * @param {IObjective} data
   */
  constructor(data?: IObjective) {
    this.needsAndObjectives = new ObjectiveNeedsAndObjectives(data?.needsAndObjectives);
    this.requirementsAndObjectives = new ObjectiveRequirementsAndObjectives(
      data?.requirementsAndObjectives
    );
    this.actingAsAGuarantor = new ObjectiveActingAsAGuarantor(data?.actingAsAGuarantor);
    this.possibleAdverseChangesToFinancialSituation = new ObjectivePossibleAdverseChangesToFinancialSituation(
      data?.possibleAdverseChangesToFinancialSituation
    );
    this.possibleBeneficialChangesToFinancialSituation = new ObjectivePossibleBeneficialChangesToFinancialSituation(
      data?.possibleBeneficialChangesToFinancialSituation
    );
    this.exitStrategy = new ObjectiveExitStrategy(data?.exitStrategy);
    this.rateType = new ObjectiveRateType(data?.rateType);
    this.repaymentType = new ObjectiveRepaymentType(data?.repaymentType);
    this.productType = new ObjectiveProductType(data?.productType);
    this.conflictsBetweenLoanFeatures = new ObjectiveConflictsBetweenLoanFeatures(
      data?.conflictsBetweenLoanFeatures
    );
    this.signedDocuments = new ObjectiveSignedDocuments(data?.signedDocuments);
    this.otherComments = data?.otherComments || '';
    this.whatIsImportantForYou = new ObjectiveWhatIsImportantForYou(
      data?.whatIsImportantForYou
    );
    this.termOfCreditSought = new ObjectiveTermOfCreditSought(data?.termOfCreditSought);
    this.financialSecurity = new ObjectiveFinancialSecurity(data?.financialSecurity);
    this.creditHistory = new ObjectiveCreditHistory(data?.creditHistory);
    this.health = new ObjectiveHealth(data?.health);
    this.additionalServices = new ObjectiveAdditionalServices(data?.additionalServices);
    this.productRequirements = new ObjectiveProductRequirements(data?.productRequirements);
    this.archive = !isEmpty(data?.archive) ? new ObjectiveArchive(data?.archive) : undefined;
  }
}
